/**
 * Modern Product Details - Enhanced Interactions
 * Fluid and responsive animations for better UX
 */

class ModernProductDetails {
  constructor(productElement) {
    this.product = productElement;
    this.init();
  }

  init() {
    this.setupMediaGallery();
    this.setupAccordions();
    this.setupPriceAnimation();
    this.setupFormInteractions();
    this.setupStickyBehavior();
    this.observeIntersections();
  }

  /**
   * Setup Media Gallery with modern interactions
   */
  setupMediaGallery() {
    const mediaItems = this.product.querySelectorAll('.product__media-item');
    const thumbnails = this.product.querySelectorAll('.thumbnail');

    thumbnails.forEach((thumb) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        this.updateActiveMedia(thumb);
      });

      // Keyboard navigation
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.updateActiveMedia(thumb);
        }
      });
    });

    // Image zoom on hover
    mediaItems.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        item.classList.add('is-active-hover');
      });
      item.addEventListener('mouseleave', () => {
        item.classList.remove('is-active-hover');
      });
    });
  }

  /**
   * Update active media item
   */
  updateActiveMedia(thumbnail) {
    const thumbnails = this.product.querySelectorAll('.thumbnail');
    const mediaItems = this.product.querySelectorAll('.product__media-item');

    thumbnails.forEach((t) => {
      t.removeAttribute('aria-current');
      t.classList.remove('active');
    });

    mediaItems.forEach((item) => {
      item.classList.remove('is-active');
    });

    thumbnail.setAttribute('aria-current', 'true');
    thumbnail.classList.add('active');

    // Find and activate corresponding media item
    const index = Array.from(thumbnails).indexOf(thumbnail);
    if (mediaItems[index]) {
      mediaItems[index].classList.add('is-active');
    }
  }

  /**
   * Setup smooth accordion animations
   */
  setupAccordions() {
    const accordions = this.product.querySelectorAll('.product__accordion details');

    accordions.forEach((accordion, index) => {
      // Smooth open/close animation
      accordion.addEventListener('toggle', (e) => {
        if (e.target.open) {
          e.target.style.overflow = 'hidden';
          const content = e.target.querySelector('.accordion__content');
          if (content) {
            e.target.style.maxHeight = content.scrollHeight + 50 + 'px';
            setTimeout(() => {
              e.target.style.overflow = 'visible';
            }, 300);
          }
        } else {
          e.target.style.overflow = 'hidden';
          e.target.style.maxHeight = '0';
        }
      });
    });
  }

  /**
   * Animate price on change
   */
  setupPriceAnimation() {
    const priceContainer = this.product.querySelector('.product .price');
    if (priceContainer) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            priceContainer.classList.add('price-updated');
            setTimeout(() => {
              priceContainer.classList.remove('price-updated');
            }, 300);
          }
        });
      });

      observer.observe(priceContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  /**
   * Form interactions and validation feedback
   */
  setupFormInteractions() {
    const form = this.product.querySelector('.product-form');
    const inputs = this.product.querySelectorAll('.product-form__input input, .product-form__input select');

    inputs.forEach((input) => {
      // Focus animation
      input.addEventListener('focus', () => {
        input.parentElement?.classList.add('input-focused');
      });

      input.addEventListener('blur', () => {
        input.parentElement?.classList.remove('input-focused');
      });

      // Smooth state transitions
      input.addEventListener('change', () => {
        input.parentElement?.classList.add('input-changed');
        setTimeout(() => {
          input.parentElement?.classList.remove('input-changed');
        }, 200);
      });
    });

    // Form submission with smooth feedback
    if (form) {
      form.addEventListener('submit', (e) => {
        const submitBtn = form.querySelector('.product-form__submit');
        if (submitBtn) {
          submitBtn.classList.add('loading');
        }
      });
    }
  }

  /**
   * Setup sticky product info on scroll
   */
  setupStickyBehavior() {
    const infoWrapper = this.product.querySelector('.product__info-wrapper');
    const mediaWrapper = this.product.querySelector('.product__media-wrapper');

    if (!infoWrapper || !mediaWrapper) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            infoWrapper.classList.remove('is-sticky');
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(mediaWrapper);
  }

  /**
   * Intersection Observer for animations on scroll
   */
  observeIntersections() {
    const elementsToAnimate = this.product.querySelectorAll(
      '.product__accordion, .product-details__pill, .product__text'
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    elementsToAnimate.forEach((el) => {
      observer.observe(el);
    });
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const productElement = document.querySelector('product-info');
  if (productElement) {
    new ModernProductDetails(productElement);
  }
});

// Support for Shopify's section reload
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const productElement = event.detail.target.querySelector('product-info');
    if (productElement) {
      new ModernProductDetails(productElement);
    }
  });
}

